import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  Col,
  Label,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { customReactSelectStyles } from "../../../../styles/ReactSelectStyles";
import { H4, Image } from "../../../../AbstractElements";
import { dynamicImage } from "../../../../service";
import MasterAccesRights from "../../../../utils/MasterAccessRights.json";

interface OptionType {
  value: string;
  label: string;
  image?: string;
}

interface AccessRight {
  key: string;
  label: string;
  value: boolean;
}

interface AccessRights {
  [module: string]: AccessRight[];
}

const roleOptions: OptionType[] = [
  { value: "sales", label: "Sales" },
  { value: "developers", label: "Developers" },
  { value: "support", label: "Support" },
];

const nameOptions: Record<string, OptionType[]> = {
  sales: [
    { value: "john", label: "John Doe", image: "dashboard-8/user1.png" },
    { value: "jane", label: "Jane Smith", image: "dashboard-8/user2.png" },
    { value: "bob", label: "Bob Johnson", image: "dashboard-8/user3.png" },
  ],
  developers: [
    { value: "alice", label: "Alice Brown", image: "dashboard-8/user1.png" },
    {
      value: "charlie",
      label: "Charlie Davis",
      image: "dashboard-8/user2.png",
    },
    { value: "emma", label: "Emma Wilson", image: "dashboard-8/user3.png" },
  ],
  support: [
    { value: "mike", label: "Mike Taylor", image: "dashboard-8/user1.png" },
    { value: "sarah", label: "Sarah Miller", image: "dashboard-8/user2.png" },
    { value: "david", label: "David Lee", image: "dashboard-8/user3.png" },
  ],
};

interface MasterAccessDropdownProps {
  selectedRole: OptionType | null;
  setSelectedRole: React.Dispatch<React.SetStateAction<OptionType | null>>;
  selectedName: OptionType | null;
  setSelectedName: React.Dispatch<React.SetStateAction<OptionType | null>>;
  availableNames: OptionType[];
  setAvailableNames: React.Dispatch<React.SetStateAction<OptionType[]>>;
}

const MasterAccessDropdown: React.FC<MasterAccessDropdownProps> = ({
  selectedRole,
  setSelectedRole,
  selectedName,
  setSelectedName,
  availableNames,
  setAvailableNames,
}) => {
  const { t } = useTranslation();






  useEffect(() => {
    if (selectedRole) {
      const names = nameOptions[selectedRole.value] || [];
      setAvailableNames(names);
      if (!selectedName) {
        setSelectedName(names[0] || null);
      }
    } else {
      setAvailableNames([]);
      setSelectedName(null);
    }
  }, [selectedRole, selectedName, setAvailableNames, setSelectedName]);

  return (
    <div>
      <Row className="pt-2">
        <Col sm="4" className="mb-3">
          <Label>{t("用户角色")}</Label>
          <Select
            options={roleOptions}
            value={selectedRole}
            isClearable={true}
            onChange={(option) => {
              setSelectedRole(option as OptionType);
              setSelectedName(null);
            }}
            placeholder={t("Select Role")}
            styles={customReactSelectStyles}
            classNamePrefix="select"
          />
        </Col>
        <Col sm="4" className="mb-3">
          <Label>{t("User Name")}</Label>
          <Select
            options={availableNames}
            value={selectedName}
            isClearable={true}
            onChange={(option) => setSelectedName(option as OptionType)}
            placeholder={t("Select Name")}
            isDisabled={!selectedRole}
            styles={customReactSelectStyles}
            classNamePrefix="select"
            formatOptionLabel={(option) => (
              <div className="d-flex align-items-center">
                <Image
                  src={dynamicImage(option.image || "/default-image.png")}
                  alt={option.label}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    marginRight: 8,
                  }}
                />
                <span>{option.label}</span>
              </div>
            )}
          />
        </Col>
       
      </Row>

  
    </div>
  );
};

function MasterAccessTab({ basicTab, id }: { basicTab: string; id?: string }) {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState<OptionType | null>(null);
  const [selectedName, setSelectedName] = useState<OptionType | null>(null);
  const [availableNames, setAvailableNames] = useState<OptionType[]>([]);
  const [mappedAccessRights, setMappedAccessRights] =
    useState<AccessRights>(MasterAccesRights);

  useEffect(() => {
    if (selectedRole) {
      const names = nameOptions[selectedRole.value] || [];
      setAvailableNames(names);
      if (!selectedName) {
        setSelectedName(names[0] || null);
      }
    } else {
      setAvailableNames([]);
      setSelectedName(null);
    }
  }, [selectedRole, selectedName]);

  useEffect(() => {
    setMappedAccessRights(MasterAccesRights);
  }, [selectedRole, selectedName]);

  return (
    <>
      {basicTab === "2" && !id && (
        <Col>
          <MasterAccessDropdown
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            selectedName={selectedName}
            setSelectedName={setSelectedName}
            availableNames={availableNames}
            setAvailableNames={setAvailableNames}
          />
          {Object.keys(mappedAccessRights).length > 0 ? (
            <Row>
              <Col>
                <Card className="height-equal">
                  <CardBody className="common-flex flex-column switch-wrapper">
                    {Object.entries(mappedAccessRights).map(
                      ([main, rights]) => (
                        <div key={main} className="mb-4">
                          <H4 className="mb-4">{main}</H4>
                          {Array.isArray(rights) &&
                            rights.map((data) => (
                              <div
                                key={data.key}
                                className="d-flex justify-content-between align-items-center mb-2 px-2"
                              >
                                <span className="text-start">{data.label}</span>
                                <Label className="switch mb-0">
                                  <Input
                                    type="checkbox"
                                    defaultChecked={data.value}
                                  />
                                  <span className="switch-state"></span>
                                </Label>
                              </div>
                            ))}
                        </div>
                      )
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          ) : (
            <Col>
              <div className="text-danger text-center mt-4 mb-4">
                {t("No user access found for this user.")}
              </div>
            </Col>
          )}
        </Col>
      )}
    </>
  );
}

export default MasterAccessTab;